import React, { ComponentProps } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import FilterChip from 'components/admin/search/FilterChip';
import { AdminCompanySearchRequest } from 'types/admin-search-companies/AdminCompanySearchRequest';
import AdminSearchCompaniesQueryHelper from './AdminSearchCompaniesQueryHelper';
import { useAdminLookup } from 'services/api/queries';
import { AdminLookupKeyEnum } from 'types/types';

interface Props {
  filter: AdminCompanySearchRequest;
}

const AdminSearchCompaniesRemoveFilterChips: React.FC<Props> = ({ filter }) => {
  const history = useHistory();
  const location = useLocation();
  const countries = useAdminLookup(AdminLookupKeyEnum.COUNTRIES).data;
  const companyRoles = useAdminLookup(AdminLookupKeyEnum.COMPANY_ROLES).data;

  if (!countries || !companyRoles) {
    return null;
  }

  const updateFilters = (newFilter: Partial<AdminCompanySearchRequest>) => {
    const newQuery = AdminSearchCompaniesQueryHelper.stringify({
      ...filter,
      ...newFilter,
      pageNumber: 1,
    });
    history.push(location.pathname + newQuery);
  };

  const chipsProps: ComponentProps<typeof FilterChip>[] = [];
  if (filter.name) {
    chipsProps.push({
      text: filter.name.trim() === '' ? `"${filter.name}"` : filter.name,
      onClick: () => updateFilters({ name: '' }),
    });
  }

  for (const countryId of filter.countryIds) {
    chipsProps.push({
      text: countries.find((c) => c.value === countryId)?.label ?? 'Unknown',
      onClick: () => updateFilters({ countryIds: filter.countryIds.filter((c) => c !== countryId) }),
    });
  }

  if (filter.size) {
    chipsProps.push({
      text: filter.size,
      onClick: () => updateFilters({ size: null }),
    });
  }

  if (filter.roleId) {
    chipsProps.push({
      text: companyRoles.find((r) => r.value === filter.roleId)?.label ?? 'Unknown',
      onClick: () => updateFilters({ roleId: null }),
    });
  }

  if (filter.verifierType) {
    chipsProps.push({
      text: filter.verifierType,
      onClick: () => updateFilters({ verifierType: null }),
    });
  }

  if (filter.includeArchived) {
    chipsProps.push({
      text: 'Archived',
      onClick: () => updateFilters({ includeArchived: false }),
    });
  }

  if (filter.onlyHolding) {
    chipsProps.push({
      text: 'Holding companies',
      onClick: () => updateFilters({ onlyHolding: false }),
    });
  }

  if (filter.onlyProcessCertification) {
    chipsProps.push({
      text: 'Process certification',
      onClick: () => updateFilters({ onlyProcessCertification: false }),
    });
  }

  return chipsProps.map((c, i) => <FilterChip key={i} {...c} />);
};

export default AdminSearchCompaniesRemoveFilterChips;
